import React, { useContext, useEffect, useRef, useState } from "react";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import {
  AppBar,
  Box,
  Button,
  DialogContentText,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { useClient, useMutation, useQuery } from "urql";
import { maskTypename } from "@urql/core";

import {
  AlertDialog,
  Backdrop,
  DataGrid,
  NoAccess,
} from "../../../../components";
import { AssetsDialog } from "../../../Workspace/AssetsCard/AssetsDialog";
import { SearchDialog } from "./SearchDialog";

import { useMemoCompare } from "../../../../services/hooks";
import { setTitle, uploadFileToS3 } from "../../../../services/utils";
import { ProgramsContext } from "../../../../programsProvider";
import { UserContext } from "../../../../userProvider";

import { useStore } from "../../../../services/hooks";

const AdminProgramMetafiles = () => {
  const [search, searchStore] = useStore("search");
  const navigate = useNavigate();
  const { programID } = useParams();
  const programs = useContext(ProgramsContext);
  const programPermissions = programs.find((p) => p.id === programID);
  const [program, setProgram] = useState(null);

  useEffect(() => {
    async function getProgram() {
      const response = await client
        .query(getProgramQuery, { id: programID })
        .toPromise();
      setProgram(response.data.programByID);
    }
    if (!program) {
      getProgram(programID);
    }
  });

  const hiddenFileInputRef = useRef(null);

  const [expanded, setExpanded] = useState(false);

  const [alertDialogProps, setAlertDialogProps] = useState({
    open: false,
  });
  const [assetDialogProps, setAssetDialogProps] = useState({
    open: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(null);

  const client = useClient();

  useEffect(() => {
    if (!searchStore.client) searchStore.init(client);
  }, [client]);

  const [searchDialogProps, setSearchDialogProps] = useState({
    metafile: null,
    programID: null,
    program: null,
    open: false,
  });

  const [currentMetafile, setCurrentMetafile] = useState(null);

  const [selectedMetafileAssociations, setSelectedMetafileAssociations] =
    useState([]);
  const [assocTitle, setAssocTitle] = useState("Associations");
  const [loading, setLoading] = useState(false);

  const [getMetafilesResponse] = useQuery({
    query: getProgramQuery,
    requestPolicy: "network-only",
    variables: { id: programID },
  });

  const [, mutateProgram] = useMutation(updateProgramMutation);

  const refreshAssociations = async (index) => {
    setLoading(true);
    const displayArray = [];
    if (program.metafileAssets[index]?.itemAssociations?.length > 0) {
      const results = await client
        .query(getItemsByIDsQuery, {
          ids: program.metafileAssets[index].itemAssociations,
        })
        .toPromise();
      results.data.itemsByIDs?.forEach((element) => {
        displayArray.push({ id: element.id, name: element.name, type: "Item" });
      });
    }
    if (program.metafileAssets[index]?.passageAssociations?.length > 0) {
      const results = await client
        .query(getPassagesByIDsQuery, {
          ids: program.metafileAssets[index].passageAssociations,
        })
        .toPromise();

      results.data.passagesByIDs.forEach((element) => {
        displayArray.push({
          id: element.id,
          name: element.name,
          type: "Passage",
        });
      });
    }
    setSelectedMetafileAssociations(displayArray);
    setAssocTitle(program.metafileAssets[index].name + " Associations");
    setCurrentMetafile(program.metafileAssets[index]);
    setExpanded(true);
    setSelectedIndex(index);
    setLoading(false);
  };

  const displayAssociations = (selectedRow) => async () => {
    if (!selectedRow) return;
    const index = program.metafileAssets.findIndex(
      (element) => element.name === selectedRow?.name,
    );
    refreshAssociations(index);
  };

  const saveProgram = (programData) => async (metafileAssets) => {
    const { id, ...programInput } = programData;
    programInput.metafileAssets = metafileAssets;
    await mutateProgram({
      data: maskTypename({
        ...programInput,
        metafileAssets,
      }),
      id: programID,
    }).then((result) => {
      if (result.error) {
        console.error("Program Mutation error:", result.error);
      }
    });
  };

  const deleteProgramMetafile = async (metafile) => {
    setLoading(true);

    const programData = getMetafilesResponse?.data;
    const metafileAssets = programData?.programByID?.metafileAssets.filter(
      (asset) => {
        return asset.name !== metafile.name;
      },
    );

    await saveProgram(programData.programByID)(metafileAssets);
    setLoading(false);
  };

  useEffect(() => {
    setTitle(["CDE", "Admin", "Programs", program?.name, "Metafiles"]);
  }, [program?.name]);

  if (!programPermissions?._actions?.includes("manageMetafiles"))
    return (
      <NoAccess message="You do not have permission to manage metafiles for this program." />
    );

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "File Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "category",
      flex: 1,
      headerName: "Category",
      type: "string",
    },
    {
      field: "comment",
      flex: 2,
      headerName: "Comment",
      type: "string",
    },
  ];

  const associationsCols = [
    {
      field: "name",
      flex: 1,
      headerName: "Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "type",
      flex: 1,
      headerName: "Type",
      type: "string",
    },
  ];

  const dataGridProps = {
    actions: [
      {
        label: "Delete",
        onClick: (row) => (e) => {
          setAlertDialogProps({
            confirmButtonText: "Delete",
            message: (
              <DialogContentText aria-label="Delete Program Metafile Alert Text">
                {`Delete Metafile ${row?.name}?`}
              </DialogContentText>
            ),
            onClose: (confirmed) => {
              setAlertDialogProps({ open: false });
              if (!confirmed) return;
              deleteProgramMetafile(row);
            },
            open: true,
            dialogTitle: `Delete Program Metafile`,
          });
        },
      },
      {
        label: "Associations",
        onClick: displayAssociations,
      },
    ],
    columns,
    error: getMetafilesResponse?.error?.message,
    getRowId: (row) => row.name,
    loading: getMetafilesResponse?.fetching,
    rows: getMetafilesResponse?.data?.programByID.metafileAssets || [],
    sortModel: [
      {
        field: "name",
        sort: "asc",
      },
    ],
  };
  const associationsGridProps = {
    actions: [
      {
        label: "Remove Association",
        onClick: (row) => (e) => {
          setAlertDialogProps({
            confirmButtonText: "Remove",
            message: (
              <DialogContentText aria-label="Remove Metafile Association Alert Text">
                {`Remove Association with ${row?.name}?`}
              </DialogContentText>
            ),
            onClose: (confirmed) => {
              setAlertDialogProps({ open: false });
              if (confirmed) {
                handleRemoveAssociation(row);
              }
            },
            open: true,
            dialogTitle: `Remove Metafile Association`,
          });
        },
      },
    ],
    columns: associationsCols,
    noRowsMessage: "No associations found.",
    rows: selectedMetafileAssociations,
  };

  const acceptableFileTypes =
    ".doc, .docx, .xls, .xlsx, .csv, .txt, .pdf, .jpg, .jpeg, .png, .eps, .gif, .svg, .mp3, .mp4, .zip, .html";

  const openFileSelector = (e) => {
    hiddenFileInputRef.current.accept = acceptableFileTypes;
    hiddenFileInputRef.current.click();
  };

  const dialogControls = [
    {
      id: "comment",
      label: "Comment",
      name: "comment",
      type: "text",
    },
    {
      id: "category",
      label: "Category",
      name: "category",
      options: [
        "Item Specification",
        "Passage Specification",
        "Copyright/DRM",
        "Other",
      ].map((option) => ({ label: option, value: option })),
      rules: {
        required: true,
      },
      type: "select",
    },
  ];

  const openMetafilesDialog = ({ file }) => {
    const fileSizeGreaterThan50MB = file?.size / 1024 ** 2 > 50;
    const filetype = file?.name.substring(
      file?.name.lastIndexOf(".", file?.name.length),
    );
    const validFileType = acceptableFileTypes.includes(filetype.toLowerCase());

    setAssetDialogProps({
      alertProps: {
        showAlert: fileSizeGreaterThan50MB || !validFileType,
        title: fileSizeGreaterThan50MB
          ? "The file size is greater than 50 MB"
          : `Invalid file type: "${filetype}"`,
        severity: "error",
      },
      btnText: "Upload",
      controlData: dialogControls,
      dialogTitle: `Upload Metafile "${file.name}"`,
      disabled: fileSizeGreaterThan50MB || !validFileType,
      maxWidth: "md",
      onClose: (asset) => {
        setAssetDialogProps({ open: false });
        if (!asset) return;
        if (file) {
          handleAssetUpload({ ...asset, file });
        }
      },
      open: true,
    });
  };

  const openSearchDialog = () => {
    setSearchDialogProps({
      dialogTitle: "Associate with " + currentMetafile?.name,
      maxWidth: "md",
      metafileIndex: selectedIndex,
      onClose: (exitCode) => {
        setSearchDialogProps({ open: false });
        if (exitCode) refreshAssociations(selectedIndex);
      },
      open: true,
      program: program,
      programID: programID,
    });
  };

  const handleFileChange =
    ({ overwrite, rows: assets }) =>
    (e) => {
      console.debug("handleFileChange.assets:", e);
      if (!e.target.files.length) return;
      const file = e.target.files[0];

      const index = assets.findIndex((o) => o.name === file.name);
      if (overwrite && index > -1) {
        setAlertDialogProps({
          confirmButtonText: "Overwrite",

          message: (
            <DialogContentText>{`A file with the name ${file.name} already exists. Overwrite the file?`}</DialogContentText>
          ),
          onClose: (confirmed) => {
            setAlertDialogProps({ open: false });
            if (confirmed) {
              handleAssetUpload({ file, ...assets[index] });
            }
          },
          open: true,
          dialogTitle: "Overwrite Asset",
        });
      } else {
        openMetafilesDialog({ file });
      }
      e.target.value = ""; // reset input value
    };

  const handleAssetUpload = async ({ file, category, comment } = {}) => {
    setLoading(true);

    const { name } = await uploadFileToS3(client)({
      addTimestampToName: false,
      file,
    });
    const asset = {
      category,
      comment,
      fileTimestamp: new Date().toISOString(),
      name,
      itemAssociations: [],
      passageAssociations: [],
    };

    let metafileAssets = dataGridProps.rows;
    metafileAssets =
      metafileAssets.length < 1
        ? [asset]
        : dataGridProps.rows.find((row) => row.name === asset.name)
          ? dataGridProps.rows.map((row) => {
              if (row.name === asset.name) {
                return { ...row, ...asset };
              } else {
                return { ...row };
              }
            })
          : [...metafileAssets, asset];

    await saveProgram(getMetafilesResponse.data.programByID)(metafileAssets);
    const response = await client
      .query(getProgramQuery, { id: programID })
      .toPromise();
    await setProgram(response.data.programByID);

    setLoading(false);
  };

  const handleRemoveAssociation = async (row) => {
    setLoading(true);
    let updatedMetafiles = program.metafileAssets;
    const index = program.metafileAssets.findIndex(
      (element) => element.name === currentMetafile?.name,
    );
    if (row.type === "Passage") {
      const updatedAssociations = program.metafileAssets[
        index
      ].passageAssociations.filter((element) => {
        return element !== row.id;
      });
      updatedMetafiles[index].passageAssociations = updatedAssociations;
    } else {
      const updatedAssociations = program.metafileAssets[
        index
      ].itemAssociations.filter((element) => {
        return element !== row.id;
      });
      updatedMetafiles[index].itemAssociations = updatedAssociations;
    }

    await saveProgram(getMetafilesResponse.data.programByID)(updatedMetafiles);
    await refreshAssociations(index);

    setLoading(false);
  };

  return (
    <>
      <AppBar color="secondary" position="sticky" sx={{ zIndex: "drawer" }}>
        <Toolbar
          sx={{
            justifyContent: "space-between",
          }}
          variant="dense"
        >
          {program && (
            <Box sx={{ display: "flex" }}>
              <Typography sx={{ marginRight: 2 }} variant="subtitle1">
                Program:
              </Typography>
              <Typography id="programName" variant="subtitle1">
                {program.name}
              </Typography>
            </Box>
          )}
          <Button
            color="primary"
            onClick={() => navigate("/admin/programs")}
            size="small"
            variant="contained"
          >
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <Backdrop loading={loading} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginX: 2,
            marginY: 1,
          }}
        >
          <Typography variant="h4">Manage Program Metafiles</Typography>
          <Button
            onClick={openFileSelector}
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Add Program Metafile
          </Button>
        </Box>
        <input
          onChange={handleFileChange({
            overwrite: true,
            rows: dataGridProps.rows,
          })}
          ref={hiddenFileInputRef}
          style={{ display: "none" }}
          type="file"
        ></input>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            marginX: 2,
            marginY: 1,
          }}
        >
          {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
          {assetDialogProps.open && <AssetsDialog {...assetDialogProps} />}
          {searchDialogProps.open && <SearchDialog {...searchDialogProps} />}
          <Box sx={{ flexGrow: 1, minHeight: "500px" }}>
            <DataGrid key="programMetafiles" {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      </Box>
      {expanded && (
        <Box
          aria-label="associations panel expanded"
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              marginX: 2,
              marginY: 1,
            }}
          >
            <Typography variant="h5">{assocTitle}</Typography>
            <Button
              onClick={openSearchDialog}
              size="small"
              startIcon={<PlusIcon />}
              variant="outlined"
            >
              Add Association
            </Button>
          </Box>
          <Paper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              marginX: 2,
              marginY: 1,
            }}
          >
            <Box sx={{ flexGrow: 1, minHeight: "500px" }}>
              <DataGrid
                key="rowAssociations"
                {...associationsGridProps}
              ></DataGrid>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

const programFields = `
  id
  name
  metafileAssets {
    category
    comment
    fileTimestamp
    itemAssociations
    name
    passageAssociations
  }
`;

const getProgramQuery = `
  query GetProgram($id: ID!) {
    programByID(id: $id) { ${programFields} }
  }`;

const getItemsByIDsQuery = `
  query getItemsByIDs($ids: [ID!]!) {
    itemsByIDs(ids: $ids) {
      id
      name
    }
  }
`;

const getPassagesByIDsQuery = `
query getPassagesByIDs($ids: [ID!]!) {
  passagesByIDs(ids: $ids) {
    id
    name
  }
}
`;

const updateProgramMutation = `
  mutation UpdateProgram($id: ID!, $data: ProgramInput!) {
    updateProgram(id: $id, data: $data) { ${programFields} }
  }`;

export {
  AdminProgramMetafiles,
  getProgramQuery,
  getPassagesByIDsQuery,
  getItemsByIDsQuery,
  updateProgramMutation,
};
